import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { SensitiveValue } from '@/components/privacy/SensitiveText';
import { monumentaChartColors, monumentaChartTheme } from '@/lib/components/charts/monumenta-chart-theme';

interface MonthlyData {
  month: string
  receita: number
  custos: number
  receitaFinanceira?: number
  // Individual cost breakdown
  folha?: number
  folhaComum?: number
  maoDeObra?: number
  bonificacao?: number
  custosDiretos?: number
  custosADM?: number
  impostos?: number
}

interface RevenueCostChartProps {
  data?: MonthlyData[]
}

export function RevenueCostChart({ data: inputData }: RevenueCostChartProps) {
  // Backend sends:
  // - receita: receitaBruta (Receita Bruta - gross revenue before taxes)
  // - custos: impostos + folha + despesasOperacionais + servicosPrestados
  //   (taxes + payroll + ADM costs + direct costs)
  const data = (inputData || []).map(m => ({ ...m, resultado: m.receita - m.custos }));

  // Calculate metrics
  if (data.length > 0) {
    console.log('\n');
    console.log('╔════════════════════════════════════════════════════════════════════════════╗');
    console.log('║                    REVENUE-COST CHART MATRIX                               ║');
    console.log('╚════════════════════════════════════════════════════════════════════════════╝');

    // Create detailed matrix header with all cost categories including folha breakdown
    console.log('\n📊 DETAILED MONTHLY MATRIX (All Cost Categories with Folha Breakdown):');
    console.log('┌──────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┐');
    console.log('│  Month   │   Receita    │ Custos Dir   │ Folha Comum  │ Mão de Obra  │ Bonificação  │  Custos ADM  │   Impostos   │ Total Custos │    Margin    │');
    console.log('├──────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┤');

    // Monthly rows with individual costs
    data.forEach((month) => {
      const receita = month.receita / 1000000;
      const custosDiretos = (month.custosDiretos || 0) / 1000000;
      const folhaComum = (month.folhaComum || 0) / 1000000;
      const maoDeObra = (month.maoDeObra || 0) / 1000000;
      const bonificacao = (month.bonificacao || 0) / 1000000;
      const custosADM = (month.custosADM || 0) / 1000000;
      const impostos = (month.impostos || 0) / 1000000;
      const totalCustos = month.custos / 1000000;
      const margin = (month.receita - month.custos) / 1000000;

      console.log(
        `│ ${month.month.padEnd(8)} │ R$ ${receita.toFixed(2).padStart(8)}M │ R$ ${custosDiretos.toFixed(2).padStart(8)}M │ R$ ${folhaComum.toFixed(2).padStart(8)}M │ R$ ${maoDeObra.toFixed(2).padStart(8)}M │ R$ ${bonificacao.toFixed(2).padStart(8)}M │ R$ ${custosADM.toFixed(2).padStart(8)}M │ R$ ${impostos.toFixed(2).padStart(8)}M │ R$ ${totalCustos.toFixed(2).padStart(8)}M │ R$ ${margin.toFixed(2).padStart(8)}M │`
      );
    });

    // Yearly totals
    const yearlyReceita = data.reduce((sum, m) => sum + m.receita, 0);
    const yearlyCustos = data.reduce((sum, m) => sum + m.custos, 0);
    const yearlyMargin = yearlyReceita - yearlyCustos;

    console.log('├──────────┼──────────────┼──────────────┼──────────────┼──────────────┤');
    console.log(
      `│ TOTAL    │ R$ ${(yearlyReceita / 1000000).toFixed(2).padStart(8)}M │ R$ ${(yearlyCustos / 1000000).toFixed(2).padStart(8)}M │ R$ ${(yearlyMargin / 1000000).toFixed(2).padStart(8)}M │ ${((yearlyMargin / yearlyReceita * 100).toFixed(1)).padStart(10)}% │`
    );
    console.log('└──────────┴──────────────┴──────────────┴──────────────┴──────────────┘');

    // Summary stats
    console.log('\n💰 SUMMARY STATISTICS:');
    console.log('┌─────────────────────────────┬──────────────────┐');
    console.log(`│ Total Months                │ ${data.length.toString().padStart(16)} │`);
    console.log(`│ Avg Monthly Receita         │ R$ ${(yearlyReceita / data.length / 1000000).toFixed(2).padStart(10)}M │`);
    console.log(`│ Avg Monthly Custos          │ R$ ${(yearlyCustos / data.length / 1000000).toFixed(2).padStart(10)}M │`);
    console.log(`│ Avg Monthly Margin          │ R$ ${(yearlyMargin / data.length / 1000000).toFixed(2).padStart(10)}M │`);
    console.log('└─────────────────────────────┴──────────────────┘');
    console.log('\n');
  }

  const formatCurrency = (value: number) => {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  };

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 card-hover shadow-monumenta">
        <div className="mb-6">
          <h3 className="text-white font-display text-xl font-semibold mb-1">Receita vs Custos</h3>
          <p className="text-brand-gray font-body text-sm">Evolução mensal nos últimos 12 meses</p>
        </div>
        <div className="flex items-center justify-center h-[300px]">
          <p className="text-brand-gray font-body text-sm">Nenhum dado disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 card-hover shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold">Receita vs Custos</h3>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray={monumentaChartTheme.grid.strokeDasharray} stroke={monumentaChartColors.background.secondary} />
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: monumentaChartColors.text.secondary }}
          />
          <YAxis
            stroke={monumentaChartColors.text.secondary}
            tick={{ fontSize: 10, fill: monumentaChartColors.text.secondary }}
            tickFormatter={formatCurrency}
          />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1.5} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div style={{
                    ...monumentaChartTheme.tooltip.contentStyle,
                  }}>
                    <p style={{ color: monumentaChartColors.text.primary, marginBottom: "5px", fontSize: '11px' }}>{label}</p>
                    {payload.map((entry: any, index: number) => (
                      <p key={index} style={{ color: entry.color, marginBottom: "3px", fontSize: '11px' }}>
                        {entry.name}: <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(entry.value as number)}
                        </SensitiveValue>
                      </p>
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend
            wrapperStyle={monumentaChartTheme.legend.wrapperStyle}
          />
          <Line
            type="monotone"
            dataKey="receita"
            stroke={monumentaChartColors.primary}
            strokeWidth={1}
            dot={{ fill: monumentaChartColors.primary, r: 3 }}
            name="Receita Bruta"
          />
          <Line
            type="monotone"
            dataKey="custos"
            stroke="#F87171"
            strokeWidth={1}
            dot={{ fill: "#F87171", r: 3 }}
            name="Custos + Despesas + Folha + Impostos"
          />
          <Line
            type="monotone"
            dataKey="resultado"
            stroke="#838998"
            strokeWidth={1}
            strokeDasharray="5 3"
            dot={{ fill: "#838998", r: 3 }}
            name="Resultado"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
